import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';

const SearchAndSort = ({ 
  searchQuery, 
  onSearchChange, 
  sortBy, 
  onSortChange, 
  viewMode, 
  onViewModeChange,
  resultsCount,
  onToggleFilters,
  showMobileFilters
}) => {
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const sortOptions = [
    { value: 'relevance', label: 'Best Match' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'rating', label: 'Customer Rating' },
    { value: 'newest', label: 'Newest First' },
    { value: 'popular', label: 'Most Popular' },
    { value: 'name', label: 'Name A-Z' }
  ];

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    // Search is handled by onChange, but we can add analytics here
    console.log('Search submitted:', searchQuery);
  };

  const clearSearch = () => {
    onSearchChange('');
  };

  return (
    <div className="bg-white border-b border-border sticky top-16 z-30">
      <div className="p-4 space-y-4">
        {/* Search Bar */}
        <form onSubmit={handleSearchSubmit} className="relative">
          <div className={`relative transition-all duration-200 ${
            isSearchFocused ? 'ring-2 ring-primary ring-opacity-50' : ''
          }`}>
            <Icon 
              name="Search" 
              size={20} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary"
            />
            <input
              type="text"
              placeholder="Search products, brands, categories..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e?.target?.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              className="w-full pl-10 pr-10 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-secondary hover:text-text-primary"
              >
                <Icon name="X" size={16} />
              </button>
            )}
          </div>
        </form>

        {/* Controls Row */}
        <div className="flex items-center justify-between gap-4">
          {/* Left Side - Filters & Results */}
          <div className="flex items-center space-x-4">
            {/* Mobile Filter Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={onToggleFilters}
              iconName="Filter"
              iconPosition="left"
              className="lg:hidden"
            >
              Filters
              {showMobileFilters && (
                <span className="ml-1 bg-primary text-white text-xs px-1.5 py-0.5 rounded-full">
                  Active
                </span>
              )}
            </Button>

            {/* Results Count */}
            <div className="hidden sm:flex items-center text-sm text-text-secondary">
              <span className="font-medium text-text-primary">{resultsCount}</span>
              <span className="ml-1">
                {resultsCount === 1 ? 'product' : 'products'} found
              </span>
            </div>
          </div>

          {/* Right Side - Sort & View */}
          <div className="flex items-center space-x-3">
            {/* Sort Dropdown */}
            <div className="min-w-0 flex-shrink-0">
              <Select
                options={sortOptions}
                value={sortBy}
                onChange={onSortChange}
                placeholder="Sort by"
                className="w-40"
              />
            </div>

            {/* View Mode Toggle */}
            <div className="hidden md:flex items-center border border-border rounded-lg p-1">
              <button
                onClick={() => onViewModeChange('grid')}
                className={`p-2 rounded transition-colors duration-200 ${
                  viewMode === 'grid' ?'bg-primary text-white' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                }`}
                title="Grid view"
              >
                <Icon name="Grid3X3" size={16} />
              </button>
              <button
                onClick={() => onViewModeChange('list')}
                className={`p-2 rounded transition-colors duration-200 ${
                  viewMode === 'list' ?'bg-primary text-white' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                }`}
                title="List view"
              >
                <Icon name="List" size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Results Count */}
        <div className="sm:hidden text-sm text-text-secondary">
          <span className="font-medium text-text-primary">{resultsCount}</span>
          <span className="ml-1">
            {resultsCount === 1 ? 'product' : 'products'} found
          </span>
        </div>

        {/* Active Search Query */}
        {searchQuery && (
          <div className="flex items-center space-x-2 text-sm">
            <span className="text-text-secondary">Searching for:</span>
            <div className="flex items-center space-x-2 bg-primary/10 text-primary px-3 py-1 rounded-full">
              <span className="font-medium">"{searchQuery}"</span>
              <button
                onClick={clearSearch}
                className="hover:bg-primary/20 rounded-full p-0.5"
              >
                <Icon name="X" size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Quick Filters */}
        <div className="flex flex-wrap gap-2">
          <button className="px-3 py-1 text-sm border border-border rounded-full hover:border-primary hover:text-primary transition-colors duration-200">
            Best Sellers
          </button>
          <button className="px-3 py-1 text-sm border border-border rounded-full hover:border-primary hover:text-primary transition-colors duration-200">
            New Arrivals
          </button>
          <button className="px-3 py-1 text-sm border border-border rounded-full hover:border-primary hover:text-primary transition-colors duration-200">
            On Sale
          </button>
          <button className="px-3 py-1 text-sm border border-border rounded-full hover:border-primary hover:text-primary transition-colors duration-200">
            Free Shipping
          </button>
          <button className="px-3 py-1 text-sm border border-border rounded-full hover:border-primary hover:text-primary transition-colors duration-200">
            Eco-Friendly
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchAndSort;